import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {apiEndpoint, useUser} from "@/components/Providers/JWTProvider.tsx";

export type MembershipReportSchool = {
    id: number;
    shortName: string;
};

export type ReportMembershipType = {
    id: string;
    description: string | null;
    type: string | null;
    tier: string | null;
    schoolId: number | null;
};

export type MembershipReportAccess = {
    hasAccess: boolean;
    schools: MembershipReportSchool[];
    membershipTypes: ReportMembershipType[];
};

type MembershipReportAccessContextValue = {
    access: MembershipReportAccess;
    loading: boolean;
    error: string | undefined;
};

const NO_ACCESS: MembershipReportAccess = {
    hasAccess: false,
    schools: [],
    membershipTypes: [],
};

const membershipReportAccessContext = createContext<MembershipReportAccessContextValue>({
    access: NO_ACCESS,
    loading: false,
    error: undefined,
});

export const MembershipReportAccessProvider = ({children}: {children: ReactNode}) => {
    const [access, setAccess] = useState<MembershipReportAccess>(NO_ACCESS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | undefined>();
    const {user, isEmployee} = useUser();

    const fetchData = useCallback(async (): Promise<void> => {
        setError(undefined);
        if (!(user && isEmployee)) {
            setAccess(NO_ACCESS);
            setLoading(false);
            return;
        }

        try {
            const url = new URL("/v1/membership/report-access", apiEndpoint);
            const result = await user.apiFetch(url);

            if (!result.ok) {
                setError("Error fetching report access");
                return;
            }

            setAccess((await result.json()) as MembershipReportAccess);
        } catch (e) {
            console.error("MembershipReportAccessProvider", e);
            setError("Error fetching report access");
        } finally {
            setLoading(false);
        }
    }, [user, isEmployee]);

    useEffect(() => {
        fetchData().catch(e => {
            console.error(e);
            setError("Error fetching report access");
        });
    }, [fetchData]);

    const value = useMemo(() => ({access, loading, error}), [access, loading, error]);

    return (
        <membershipReportAccessContext.Provider value={value}>
            {children}
        </membershipReportAccessContext.Provider>
    );
};

export const useMembershipReportAccess = () => useContext(membershipReportAccessContext);
